import { useRef } from 'react'

import { Tweet } from './components/Tweet'
import { Empty } from '../Common/Empty'
import { getPostPreviewEntries } from '../Common/getPostPreviewEntries'
import { useScrollToThreadedPost } from '../Common/hooks/useScrollToThreadedPost'
import type { TweetType, TwitterDraft, TwitterProfile } from '../types'
import { PostPreviewCard } from '../Common/styles'

import type { JSX } from 'react'

export const TwitterPreview = ({
  draft,
  profile,
}: {
  draft: TwitterDraft
  profile: TwitterProfile
}): JSX.Element => {
  const { isEmpty, hasThread, activeThreadId, service } = draft
  const singlePostIdentityRef = useRef<object>({})

  const getTweets = (): TweetType[] => {
    const drafts: TweetType[] = []

    if (hasThread) {
      const { activeThreadId: currentThreadId } = draft

      draft.thread?.forEach((threadedDraft, i) => {
        drafts.push({
          content:
            currentThreadId === i
              ? {
                  text: draft.text,
                  enabledAttachmentType: draft.enabledAttachmentType,
                  images: draft.images,
                  video: draft.video,
                  videos: draft.videos,
                  gif: draft.gif,
                  link: draft.link,
                  retweet: draft.retweet,
                  assets: draft.assets,
                  inThread: true,
                }
              : {
                  ...threadedDraft,
                  inThread: true,
                },
        })
      })
    } else {
      drafts.push({
        content: {
          ...draft,
          text: draft.text,
          inThread: false,
        },
      })
    }

    return drafts
  }

  const tweets = getTweets()
  const tweetEntries = getPostPreviewEntries(
    tweets,
    (tweet) => tweet.content,
    (_tweet, index) => draft.thread?.[index] ?? singlePostIdentityRef.current,
  )

  // Disabled scroll to thread when in omni mode
  useScrollToThreadedPost(!service.isOmni ? activeThreadId : undefined)

  if (isEmpty && !hasThread) return <Empty />

  return (
    <PostPreviewCard data-testid="twitter-preview">
      {tweetEntries.map(({ key, post: tweet }, index) => (
        <Tweet
          key={key}
          content={tweet.content}
          profile={profile}
          index={index}
          isAiGenerated={draft.isAiGenerated}
        />
      ))}
    </PostPreviewCard>
  )
}
